import { lazy, type ComponentType } from 'react'

// Route chunks are fetched at navigation time, which introduces a failure mode
// the old single-bundle build did not have: a deploy replaces the hashed chunk
// files while a user still has the previous index.html running. Their next
// navigation then requests a chunk URL that 404s, the import rejects, and the
// root Sentry ErrorBoundary paints a crash screen — on every deploy, for every
// open tab.
//
// So: retry once (covers an ordinary flaky network), and if it still fails with
// what looks like a missing/failed chunk, reload the page once to pick up the
// new index.html. The sessionStorage marker makes the reload strictly one-shot,
// so a genuinely broken deploy shows the error boundary instead of trapping the
// user in a refresh loop.

const RELOAD_MARKER = 'bmc_chunk_reloaded'

// Each browser words a failed chunk differently. The MIME one is the hosting
// side of the same deploy race: Cloudflare Pages answers a missing
// /assets/*.js with the SPA index.html (200, text/html), so Safari/WebKit
// rejects it as "'text/html' is not a valid JavaScript MIME type" instead of
// reporting a fetch failure. "Unable to preload CSS" is Vite's own preload
// helper hitting the same missing-file case for a route's stylesheet.
const CHUNK_ERROR_RE = new RegExp([
  'Loading chunk',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'is not a valid JavaScript MIME type',
  'Unable to preload CSS',
].join('|'), 'i')

function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name} ${err.message}` : String(err)
  return CHUNK_ERROR_RE.test(msg)
}

export function lazyRoute<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory()
      // A clean load means whatever we may have reloaded for is resolved.
      try { sessionStorage.removeItem(RELOAD_MARKER) } catch { /* storage disabled */ }
      return mod
    } catch (first) {
      try {
        return await factory()
      } catch (err) {
        if (isChunkLoadError(err)) {
          let alreadyReloaded = false
          try { alreadyReloaded = sessionStorage.getItem(RELOAD_MARKER) === '1' } catch { /* storage disabled */ }
          if (!alreadyReloaded) {
            try { sessionStorage.setItem(RELOAD_MARKER, '1') } catch { /* storage disabled */ }
            window.location.reload()
            // Never resolves — the reload is already underway. Returning a
            // component here would flash it for a frame first.
            return new Promise<{ default: T }>(() => {})
          }
        }
        throw err
      }
    }
  })
}
